/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useRef, useCallback } from "react";
import {
  getNotifications,
  ReadNotification,
  deleteNotification,
} from "../api/user";
import DashboardHeader from "../components/Other/DashboardHeader";
import PageContainer from "../components/pages/PageContainer";
// import PageHeader from '../components/PageHeader'
import { Link } from "react-router-dom";
import { FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import CustomPageHeader from "../components/pages/CustomPageHeader";
import Loader from "../components/Loader";

const UserNotification = () => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedNotificationId, setSelectedNotificationId] = useState(null);

  const observer = useRef();

  // Infinite scroll: track last notification
  const lastNotificationRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setPage((prev) => prev + 1);
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  useEffect(() => {
    let abort = false;
    setLoading(true);
    const fetch = async () => {
      try {
        const res = await getNotifications(page);
        if (!abort) {
          let data = res?.data?.data;
          if (!data) {
            data = [];
          } else if (!Array.isArray(data)) {
            data = [data]; // normalize single object → array
          }
          setNotifications((prev) => (page === 1 ? data : [...prev, ...data]));
          setHasMore(data.length > 0);
        }
      } catch (err) {
        // Silently fail, do not set error
      } finally {
        if (!abort) setLoading(false);
      }
    };
    fetch();
    return () => {
      abort = true; // cancel on cleanup
    };
  }, [page]);

  // Handle notification click
  const handleNotificationClick = async (n) => {
    if (!n.read) {
      try {
        await ReadNotification(n.id);
        setNotifications((prev) =>
          prev.map((notif) =>
            notif.id === n.id ? { ...notif, read: true } : notif
          )
        );
      } catch (err) {
        // Optionally handle error
      }
    }
  };

  // Delete notification
  const handleDeleteClick = (id) => {
    setSelectedNotificationId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await deleteNotification(selectedNotificationId);
      // Remove notification from UI (update state)
      setNotifications((prev) =>
        prev.filter((n) => n.id !== selectedNotificationId)
      );
      setShowDeleteModal(false);
      setSelectedNotificationId(null);
      toast.success(t("notification_deleted"));
    } catch (err) {
      // Optionally show error
      toast.error(t("failed_to_delete_notification"));
    }
    setDeleting(false);
  };

  // ✅ Format full date + time with AM/PM
  const formatDateTime = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const pad = (n) => n.toString().padStart(2, "0");
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const year = date.getFullYear();
    let hour = date.getHours();
    const minute = pad(date.getMinutes());
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12;
    if (hour === 0) hour = 12;
    hour = pad(hour);
    return `${month}/${day}/${year} ${hour}:${minute} ${ampm}`;
  };

  return (
    <>
      <CustomPageHeader
        hideBack={true}
        showPageHeader={false}
        custom
        pageContainerClass="pt-5"
        title={t("notifications", "notifications")}
      >
        <div>
          <h1 className="hidden lg:block w-full text-2xl font-bold text-gray-800 dark:text-dark-text mb-4">
            {t("notifications", "Notifications")}
          </h1>

          <div className="w-full  py-6 space-y-4 bg-white-200">
            <ul className="space-y-3">
              {notifications.length === 0 && !loading && (
                <li className="text-gray-400">
                  {t("no_notifications_found", "No notifications found.")}
                </li>
              )}

              {notifications.map((n, idx) => {
                const isUnread = n.read === false;
                const classes = isUnread
                  ? "bg-[#bba4f2] dark:bg-[#BBA5F2] text-black dark:text-white"
                  : "bg-[#f4f4f4] dark:bg-[#252739] text-black dark:text-dark-text";

                const vibeId = n.notification_data?.id;
                const linkTo = vibeId ? `/vibe/${vibeId}` : "#";

                const content = (
                  <div
                    className={`relative flex items-start rounded-lg shadow p-3 pr-12 gap-3 ${classes}`}
                  >
                    {/* Delete icon button */}
                    <button
                      className="absolute top-1 right-2 text-red-600 hover:bg-red-100 rounded-full p-2 z-10"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteClick(n.id);
                      }}
                      title={t("delete", "Delete")}
                    >
                      <FaTrash className="h-5 w-5" />
                    </button>
                    {/* Avatar / Image */}
                    <img
                      src={n.sender?.image || "/vibe_newlogo.png"}
                      alt={t("notification", "Notification")}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    {/* Body + Date/Time */}
                    <div className="flex-1">
                      <div className="font-semibold text-[14px]">{n.body}</div>
                      <div className="text-xs opacity-80 mt-1 text-right">
                        {formatDateTime(n.created_at)}
                      </div>
                    </div>
                  </div>
                );

                if (notifications.length === idx + 1) {
                  return (
                    <li ref={lastNotificationRef} key={n.id}>
                      <Link
                        to={linkTo}
                        state={{ backTo: "/notifications" }}
                        className="block no-underline"
                        onClick={() => handleNotificationClick(n)}
                      >
                        {content}
                      </Link>
                    </li>
                  );
                } else {
                  return (
                    <li key={n.id}>
                      <Link
                        to={linkTo}
                        state={{ backTo: "/notifications" }}
                        className="block no-underline"
                        onClick={() => handleNotificationClick(n)}
                      >
                        {content}
                      </Link>
                    </li>
                  );
                }
              })}
            </ul>

            {loading && <Loader />}
          </div>

          {/* Confirmation Modal */}
          {showDeleteModal && (
            <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
              <div className="relative w-full max-w-xs">
                {/* Logo overlapping the modal */}
                <div className="absolute left-1/2 -top-8 -translate-x-1/2 z-10">
                  <img
                    src="/logo-bg.png"
                    alt={t("vibelinks", "VibeLinks")}
                    className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                  />
                </div>
                {/* Modal container */}
                <div className="bg-white dark:bg-[#252739] rounded-2xl shadow-xl p-6 pt-12 flex flex-col items-center">
                  <h2 className="text-2xl font-bold mb-2 text-center text-black dark:text-dark-text">
                    {t("vibelinks", "VibeLinks")}
                  </h2>
                  <p className="mb-6 text-gray-800 dark:text-dark-text text-center text-base font-medium">
                    {t(
                      "are_you_sure_you_want_to_delete_the_notification",
                      "Are you sure you want to delete the notification?"
                    )}
                  </p>
                  <div className="flex gap-4 w-full justify-center">
                    <button
                      className="flex-1 px-0 py-2 bg-red-500 text-white rounded-lg font-bold text-lg"
                      onClick={() => setShowDeleteModal(false)}
                      disabled={deleting}
                    >
                      {t("no", "No")}
                    </button>
                    <button
                      className="flex-1 px-0 py-2 bg-[#6d29f6] text-white rounded-lg font-bold text-lg"
                      onClick={confirmDelete}
                      disabled={deleting}
                    >
                      {t("yes", "Yes")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </CustomPageHeader>
    </>
  );
};

export default UserNotification;
